// hooks/usePropertyUnits.ts - Спрощений під нову lease архітектуру
import { useState, useEffect, useCallback, useRef } from 'react';
import { getPropertyUnitsRequest } from '@/api';
import { UnitsSearchParams } from '@/types/api-types';
import { UnitWithLeaseInfo } from '@/types';

interface UsePropertyUnitsState {
  units: UnitWithLeaseInfo[];
  loading: boolean;
  error: string | null;
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface UsePropertyUnitsParams {
  propertyId?: number | string;
  autoFetch?: boolean;
}

export const usePropertyUnits = ({
                                   propertyId,
                                   autoFetch = true,
                                 }: UsePropertyUnitsParams = {}) => {
  const [state, setState] = useState<UsePropertyUnitsState>({
    units: [],
    loading: false,
    error: null,
    meta: {
      total: 0,
      page: 1,
      limit: 7,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
  });

  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);

  // Основна функція для завантаження юнітів
  const fetchUnits = useCallback(
    async (params: UnitsSearchParams = {}) => {
      if (!propertyId || isLoadingRef.current) {
        // console.log('Skipping fetch:', { propertyId, isLoading: isLoadingRef.current });
        return;
      }

      const numericPropertyId =
        typeof propertyId === 'string' ? parseInt(propertyId, 10) : propertyId;

      if (isNaN(numericPropertyId)) {
        setState((prev) => ({
          ...prev,
          error: 'Invalid property ID',
          loading: false,
        }));
        return;
      }

      // console.log('Starting fetch units for property:', numericPropertyId, 'params:', params);

      isLoadingRef.current = true;
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const defaultParams = {
          page: 1,
          limit: 7,
          ...params
        };

        const response = await getPropertyUnitsRequest(numericPropertyId, defaultParams);

        // console.log('Units API response:', response.data);

        // Тепер використовуємо дані напряму без адаптації
        const units = response.data.data;

        setState({
          units,
          loading: false,
          error: null,
          meta: response.data.meta,
        });

        hasLoadedRef.current = true;
        // console.log('Units loaded successfully:', units.length);

      } catch (error: any) {
        console.error('Error fetching units:', error);

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Error loading units';

        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      } finally {
        isLoadingRef.current = false;
      }
    },
    [propertyId],
  );

  // Функція для зміни сторінки
  const changePage = useCallback(
    async (page: number, params: UnitsSearchParams = {}) => {
      if (!propertyId || isLoadingRef.current) {
        return;
      }

      await fetchUnits({ ...params, page });
    },
    [fetchUnits, propertyId],
  );

  // Функція для пошуку
  const searchUnits = useCallback(
    async (searchParams: UnitsSearchParams) => {
      if (!propertyId || isLoadingRef.current) {
        return;
      }

      // Скидаємо на першу сторінку при новому пошуку
      await fetchUnits({ ...searchParams, page: 1 });
    },
    [fetchUnits, propertyId],
  );

  // ТІЛЬКИ ОДИН useEffect для початкового завантаження
  useEffect(() => {
    // console.log('useEffect called:', { propertyId, autoFetch, hasLoaded: hasLoadedRef.current });

    if (autoFetch && propertyId && !hasLoadedRef.current) {
      fetchUnits();
    }
  }, [propertyId, autoFetch, fetchUnits]);

  // Функція для ручного оновлення (очищає кеш)
  const refetch = useCallback(() => {
    hasLoadedRef.current = false;
    return fetchUnits();
  }, [fetchUnits]);

  return {
    // Дані (тепер прямо з API без адаптації)
    units: state.units,
    loading: state.loading,
    error: state.error,
    meta: state.meta,

    // Функції
    fetchUnits,
    changePage,
    searchUnits,
    refetch,

    // Допоміжні властивості
    isEmpty: state.units.length === 0 && !state.loading,
    hasError: !!state.error,

    // Статистика для UI
    totalUnits: state.meta.total,
    currentPage: state.meta.page,
    totalPages: state.meta.totalPages,
    hasNextPage: state.meta.hasNext,
    hasPrevPage: state.meta.hasPrev,
  };
};